import isEqual from 'lodash/isEqual';

import { BooleanListeningSettingKey } from 'modules/listening/features/settings/settings';
import { useListeningSettingsStore } from 'modules/listening/features/settings/settingsStore';
import { subscriptionStoreSelectors } from 'modules/subscription/stores/selectors';

import { queueSettingsChange } from '../service';
import { SharedUserSettingsEnum, UserSettingsEnum, useSettingsStore } from '../store';

interface ListeningSettingConfig {
  settingKey: SharedUserSettingsEnum;
  listeningStoreKey: BooleanListeningSettingKey;
  defaultValue: boolean;
}

/**
 * Sync Pause Strategy:
 * Local → Remote: Pause outgoing changes for specific keys during active user interaction
 * This prevents conflicts and batches rapid toggles into single updates
 */
const queuedOutgoingChanges = new Map<string, boolean>();
const pausedSettingsKeys = new Set<string>();

export const pauseSettingsSync = (keys: string[]) => {
  keys.forEach(key => pausedSettingsKeys.add(key));
};

export const resumeSettingsSync = (keys: string[]) => {
  keys.forEach(key => {
    pausedSettingsKeys.delete(key);

    if (queuedOutgoingChanges.has(key)) {
      const value = queuedOutgoingChanges.get(key);
      queuedOutgoingChanges.delete(key);

      queueSettingsChange({
        [key]: value
      });
    }
  });
};

export const pauseAllSkipContentSync = () => {
  const skipContentKeys = LISTENING_SETTINGS.filter(({ settingKey }) => settingKey.includes('autoSkipContent') || settingKey.includes('AUTO_SKIP')).map(
    ({ listeningStoreKey }) => listeningStoreKey
  );

  pauseSettingsSync(skipContentKeys);
};

export const resumeAllSkipContentSync = () => {
  const skipContentKeys = LISTENING_SETTINGS.filter(({ settingKey }) => settingKey.includes('autoSkipContent') || settingKey.includes('AUTO_SKIP')).map(
    ({ listeningStoreKey }) => listeningStoreKey
  );

  resumeSettingsSync(skipContentKeys);
};

const LISTENING_SETTINGS: ListeningSettingConfig[] = [
  {
    settingKey: SharedUserSettingsEnum.AUTO_HIDE_PLAYER,
    listeningStoreKey: UserSettingsEnum.AUTO_HIDE_INTERFACE,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SCROLL,
    listeningStoreKey: UserSettingsEnum.AUTO_SCROLL,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.CLICK_TO_LISTEN,
    listeningStoreKey: UserSettingsEnum.CLICK_TO_LISTEN,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_BRACES,
    listeningStoreKey: UserSettingsEnum.SKIP_BRACES,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_BRACKETS,
    listeningStoreKey: UserSettingsEnum.SKIP_BRACKETS,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_CITATIONS,
    listeningStoreKey: UserSettingsEnum.SKIP_CITATIONS,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_ENHANCED,
    listeningStoreKey: UserSettingsEnum.SKIP_ENHANCED,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_FOOTERS,
    listeningStoreKey: UserSettingsEnum.SKIP_FOOTERS,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_FOOTNOTES,
    listeningStoreKey: UserSettingsEnum.SKIP_FOOTNOTES,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_HEADERS,
    listeningStoreKey: UserSettingsEnum.SKIP_HEADERS,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_PARENTHESES,
    listeningStoreKey: UserSettingsEnum.SKIP_PARENTHESES,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_URLS,
    listeningStoreKey: UserSettingsEnum.SKIP_URLS,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_FORMULAS,
    listeningStoreKey: UserSettingsEnum.SKIP_FORMULAS,
    defaultValue: true
  },
  {
    settingKey: SharedUserSettingsEnum.AUTO_SKIP_TABLES,
    listeningStoreKey: UserSettingsEnum.SKIP_TABLES,
    defaultValue: true
  }
];

export function subscribe() {
  const remoteSettingsSyncChangesToSkip = new Map<string, boolean>();
  const localSyncChangesToSkip = new Map<string, boolean>();

  // Remote → Local sync
  const settingsSubscription = useSettingsStore.subscribe((state, prevState) => {
    LISTENING_SETTINGS.forEach(({ settingKey, listeningStoreKey, defaultValue }) => {
      const currentValue = state.userSettings.settings[settingKey] ?? defaultValue;
      const prevValue = prevState.userSettings.settings[settingKey] ?? defaultValue;

      const isSkipContentKey = settingKey.includes('autoSkipContent');
      const isFreeFirstDocument = subscriptionStoreSelectors.getIsFreeFirstDocument();
      const shouldSkipChange = isSkipContentKey && isFreeFirstDocument && currentValue === false;

      if (!isEqual(currentValue, prevValue) && !shouldSkipChange) {
        if (!localSyncChangesToSkip.get(settingKey) || localSyncChangesToSkip.get(settingKey) !== currentValue) {
          remoteSettingsSyncChangesToSkip.set(settingKey, currentValue as boolean);
          useListeningSettingsStore.setState(state => ({ ...state, [listeningStoreKey]: currentValue }));
        }
      }
    });

    localSyncChangesToSkip.clear();
  });

  // Local → Remote sync: Pause outgoing changes for specific keys during user interaction
  const listeningSettingsSubscription = useListeningSettingsStore.subscribe((state, prevState) => {
    LISTENING_SETTINGS.forEach(({ settingKey, listeningStoreKey }) => {
      const currentValue = state[listeningStoreKey];
      const prevValue = prevState[listeningStoreKey];

      const isSkipContentKey = settingKey.includes('autoSkipContent');
      const isFreeFirstDocument = subscriptionStoreSelectors.getIsFreeFirstDocument();
      const shouldSkipChange = isSkipContentKey && isFreeFirstDocument && currentValue === true;

      if (!isEqual(currentValue, prevValue) && !shouldSkipChange) {
        if (!remoteSettingsSyncChangesToSkip.get(settingKey) || remoteSettingsSyncChangesToSkip.get(settingKey) !== currentValue) {
          localSyncChangesToSkip.set(settingKey, currentValue);

          const isKeyPaused = pausedSettingsKeys.has(listeningStoreKey);

          if (!isKeyPaused) {
            queueSettingsChange({
              [listeningStoreKey]: currentValue
            });
          } else {
            queuedOutgoingChanges.set(listeningStoreKey, currentValue);
          }
        }
      }
    });

    remoteSettingsSyncChangesToSkip.clear();
  });

  // cleanup
  return () => {
    settingsSubscription();
    listeningSettingsSubscription();
  };
}
